/**
 * AudioWorklet-Quelltext (Doc 04 §3), zur Laufzeit aus den DSP-Klassen
 * zusammengesetzt und als Blob-URL geladen (keine separate Datei, kein CDN).
 *
 * Worklet-Vertrag:
 *   in  processorOptions: { frameSamples, targetRate, channel|null, mode, preRollMs, vad }
 *   in  port: { type:'ptt', down:boolean } | { type:'mode', mode } | { type:'reset' }
 *   out port: { type:'frame', buf:ArrayBuffer(Int16), start }   (nur gegatete Frames, transferiert)
 *             { type:'speech', state:'start'|'end', sample }
 *             { type:'level', rms, sample, vad:boolean }       (~10/s)
 *   `sample` = Index im 16-kHz-Strom seit Worklet-Start.
 */
import { EnergyVad, Int16Framer, LinearResampler, SpeechGate, type VadConfig } from './dsp.js';

export const WORKLET_PROCESSOR_NAME = 'pcm-writer';

export interface WorkletOptions {
  frameSamples: number;
  targetRate: number;
  /** Kanal 0..n-1 statt Downmix; null = Mittelwert aller Kanäle */
  channel: number | null;
  mode: 'listener' | 'ptt';
  preRollMs: number;
  vad: VadConfig;
}

export function buildWorkletSource(): string {
  return `
const LinearResampler = (${LinearResampler.toString()});
const Int16Framer = (${Int16Framer.toString()});
const EnergyVad = (${EnergyVad.toString()});
const SpeechGate = (${SpeechGate.toString()});

class PcmWriter extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = options.processorOptions;
    this.channel = o.channel;
    this.resampler = new LinearResampler(sampleRate, o.targetRate);
    this.framer = new Int16Framer(o.frameSamples);
    this.vad = new EnergyVad(o.vad);
    // Ring deckt Pre-Roll + Mindest-Sprechdauer (+ tolerierte Lücke) ab: die VAD startet erst danach.
    this.gate = new SpeechGate(o.mode, SpeechGate.preRollFramesFor(o.preRollMs + (o.vad.minSpeechMs || 0) + (o.vad.minSpeechGapMs || 0), o.frameSamples, o.targetRate));
    this.preRollSamples = Math.round((o.preRollMs * o.targetRate) / 1000);
    this.levelSum = 0;
    this.levelN = 0;
    this.levelEvery = Math.round(o.targetRate / 10);
    this.port.onmessage = (e) => this.onMessage(e.data);
  }

  onMessage(msg) {
    if (!msg) return;
    if (msg.type === 'ptt') {
      if (msg.down) this.post(this.gate.pttDown(this.framer.total));
      else this.post(this.gate.pttUp(this.framer.flushPadded()));
    } else if (msg.type === 'mode') {
      this.post(this.gate.setMode(msg.mode));
    }
  }

  post(actions) {
    for (const a of actions) {
      if (a.type === 'frame') {
        const copy = a.frame.slice();
        this.port.postMessage({ type: 'frame', buf: copy.buffer, start: a.start }, [copy.buffer]);
      } else {
        this.port.postMessage({ type: 'speech', state: a.state, sample: a.sample });
      }
    }
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0 || !input[0]) return true;
    let mono;
    if (this.channel !== null && this.channel < input.length) {
      mono = input[this.channel];
    } else if (input.length === 1) {
      mono = input[0];
    } else {
      mono = new Float32Array(input[0].length);
      for (let c = 0; c < input.length; c++) {
        const ch = input[c];
        for (let i = 0; i < mono.length; i++) mono[i] += ch[i] / input.length;
      }
    }
    const res = this.resampler.process(mono);
    for (let i = 0; i < res.length; i++) {
      this.levelSum += res[i] * res[i];
      this.levelN++;
    }
    const frames = this.framer.push(res);
    for (const f of frames) {
      const v = this.vad.processInt16(f.frame);
      let keepFrom;
      for (const e of v.events) if (e.type === 'start') keepFrom = e.sample - this.preRollSamples;
      this.post(this.gate.onFrame(f.frame, f.start, v.activeAny, keepFrom));
    }
    if (this.levelN >= this.levelEvery) {
      const rms = Math.sqrt(this.levelSum / this.levelN);
      this.port.postMessage({ type: 'level', rms: Math.min(1, rms), sample: this.framer.total, vad: this.vad.active });
      this.levelSum = 0;
      this.levelN = 0;
    }
    return true;
  }
}

registerProcessor(${JSON.stringify(WORKLET_PROCESSOR_NAME)}, PcmWriter);
`;
}

/** Blob-URL für `audioWorklet.addModule` (Browser). */
export function createWorkletUrl(): string {
  const blob = new Blob([buildWorkletSource()], { type: 'application/javascript' });
  return URL.createObjectURL(blob);
}
